import { usePathname } from 'next/navigation'
import { layoutConfig } from '@/constants/layouts'
import { getLayoutComponent, type LayoutType } from '@/utility/layoutSelector'
import { ReactNode, useMemo } from 'react'

function getLayoutType(pathname: string): LayoutType {
  const roleModelFinderDefaultLayoutPaths = [
    '/members/role-model-finder/interview',
    '/members/role-model-finder/recommendation',
  ]

  // TOPページ（/）は必ずDefaultLayout
  if (pathname === '/') {
    return 'default'
  }

  // グループページの場合はMemberLayout
  if (isGroupPage(pathname)) {
    return 'member'
  }

  // 企業記事ページ（stories, knowhows）の場合はDefaultLayout
  if (isArticlePage(pathname)) {
    return 'default'
  }

  // role-model-finderの特定のページはDefaultLayoutを返す
  if (roleModelFinderDefaultLayoutPaths.some((path) => pathname === path)) {
    return 'default'
  }

  // preview companyをチェック
  if (
    pathname.startsWith('/preview/companies/') &&
    !pathname.match(/\/(stories|knowhows)\/[^/]+\/?$/)
  ) {
    return 'company'
  }

  // 通常の企業routesマッチング
  if (matchesRoute(pathname, layoutConfig.routes.company)) {
    return 'company'
  }

  // まず具体的なroutesの条件をチェック（company routes以外）
  for (const [layoutType, routes] of Object.entries(layoutConfig.routes)) {
    if (layoutType !== 'company' && layoutType !== 'article' && matchesRoute(pathname, routes)) {
      return layoutType as LayoutType
    }
  }

  // companyパターンをチェック（/[company] 以下の全てのパス）
  // ただし、静的ルートは除外する
  if (
    layoutConfig.patterns.company.test(pathname) &&
    pathname !== '/' &&
    !isStaticRoute(pathname)
  ) {
    return 'company'
  }

  // 最後にdefaultレイアウト
  return 'default'
}

export function useLayoutSelector() {
  const pathname = usePathname()

  const layoutType = getLayoutType(pathname)

  // レイアウトタイプが変わらない限り、同じコンポーネントを返す
  return useMemo(
    () => (props: { children: ReactNode }) => getLayoutComponent(layoutType, props, pathname),
    // 意図的にpathnameを依存配列から除外したいので、ESLintを無視
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutType],
  )
}

function matchesRoute(pathname: string, routes: readonly string[]): boolean {
  return routes.some((route) => {
    // 完全一致
    if (pathname === route) {
      return true
    }

    // ルートパス '/' は完全一致のみ
    if (route === '/') {
      return false
    }

    // routeが'/'で終わる場合、pathnameがrouteで始まるかチェック
    if (route.endsWith('/') && pathname.startsWith(route)) {
      return true
    }

    // routeが'/'で終わらない場合、pathname が 'route/' で始まるかチェック
    // ただし、routeがpathnameより長い場合は除外
    if (
      !route.endsWith('/') &&
      route.length < pathname.length &&
      pathname.startsWith(route + '/')
    ) {
      return true
    }

    return false
  })
}

function isStaticRoute(pathname: string): boolean {
  // プレビュー企業ページは静的ルートから除外
  if (pathname.startsWith('/preview/companies/')) {
    return false
  }

  const firstSegment = pathname.split('/')[1]
  return (
    firstSegment !== undefined &&
    layoutConfig.staticRouteSegments.includes(
      firstSegment as (typeof layoutConfig.staticRouteSegments)[number],
    )
  )
}

function isArticlePage(pathname: string): boolean {
  // /[company]/stories/[ArticleId] または /[company]/knowhows/[ArticleId] パターンをチェック
  const articlePattern = /^\/[^/]+\/(stories|knowhows|books)\/[^/]+\/?$/
  return articlePattern.test(pathname)
}

function isGroupPage(pathname: string): boolean {
  // /[company]/group/[groupId] パターンをチェック
  const groupPattern = /^\/[^/]+\/group\/[^/]+\/?$/
  return groupPattern.test(pathname)
}
